import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { IRtcStream } from "@formant/core/model/IRtcStream";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";
import { createRtcStreamMessage } from "@formant/rtc-client/utils/createRtcStreamMessage";
import { animate } from "@formant/ui/utils/animate";

import { operateStore } from "../../../../stores";
import { ITeleopSession } from "../../../../teleop/ITeleopSession";
import { Hotkey } from "../../../common/hotkey/Hotkey";
import { StandardButton } from "../../../common/StandardButton";
import { Timer } from "../../../common/Timer";
import * as styles from "./TeleopCommandButton.scss";

export interface ITeleopCommandButtonProps {
    session: ITeleopSession;
    stream: IRtcStream;
    index: number;
    disabled?: boolean;
    statusTopic?: string;
    statusLabel?: string;
    backgroundColor?: string;
}

@observer
export class TeleopCommandButton extends Component<ITeleopCommandButtonProps> {
    @observable
    private mouseDownValue = false;
    @observable
    private value = false;
    private unregister?: () => boolean;

    public componentDidMount() {
        const { statusTopic } = this.props;
        if (statusTopic) {
            this.unregister = this.props.session.registerReceiver(
                statusTopic,
                this.onReceiveStatus,
            );
        }
    }

    public componentWillUnmount() {
        if (this.unregister) {
            this.unregister();
        }
    }

    public render() {
        const { stream, disabled, index, statusTopic, backgroundColor } =
            this.props;
        const { mouseDownValue, onMouseDown, onMouseUp } = this;
        if (!stream) {
            return false;
        }

        const concatName =
            stream.buttonLabel ||
            stream.label ||
            stream.streamName.split("/").pop();
        const hotkey =
            index >= 0 && index < 10
                ? `${(index + 1) % 10}`
                : index >= 10 && index < 20
                  ? `shift+${(index + 1) % 10}`
                  : `shift+alt+${(index + 1) % 10}`;

        return (
            <StandardButton
                className={classnames(
                    styles["teleop-command-button"],
                    backgroundColor &&
                        styles["teleop-command-button-" + backgroundColor],
                    mouseDownValue && styles.active,
                    disabled && styles.disabled,
                    statusTopic && styles.status,
                    "command-button",
                )}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                disabled={disabled}
                hotkey={!disabled ? hotkey : undefined}
                data-testid="teleop-command-button"
            >
                {statusTopic && (
                    <div
                        className={classnames(
                            styles["status-chip"],
                            styles[this.value.toString()],
                        )}
                        data-tooltip={`${statusTopic}: ${this.value.toString()}`}
                    />
                )}
                <Timer
                    interval={duration.second * 0.05}
                    onTick={this.handleGamepad}
                />
                {concatName}
                {!disabled && hotkey && (
                    <Hotkey
                        code={hotkey}
                        label={concatName || `Button ${hotkey}`}
                        onHotkey={this.onClicked}
                    />
                )}
            </StandardButton>
        );
    }

    public handleGamepad = () => {
        const { index } = this.props;
        const gamepad = operateStore.gamepad;

        if (!gamepad) {
            return;
        }

        const button =
            gamepad.buttons.length > index ? gamepad.buttons[index] : undefined;
        if (button && button.pressed) {
            this.onMouseDown();
        } else if (button && !button.pressed) {
            this.onMouseUp();
        }
    };

    private set mouseDown(value: boolean) {
        if (this.mouseDownValue !== value) {
            this.mouseDownValue = value;
            this.sendCommand(this.mouseDownValue);
        }
    }

    private onMouseDown = () => {
        this.mouseDown = true;
    };

    private onMouseUp = () => {
        this.mouseDown = false;
    };

    private onClicked = async () => {
        if (!this.mouseDown) {
            this.mouseDown = true;
            await animate();
            this.mouseDown = false;
        }
    };

    private sendCommand = (value: boolean) => {
        const { session, stream } = this.props;

        if (stream) {
            session.send(
                createRtcStreamMessage(
                    stream,
                    stream.label
                        ? {
                              bitset: {
                                  bits: [{ key: stream.label, value }],
                              },
                          }
                        : {
                              boolean: value,
                          },
                ),
            );
        }
    };

    private onReceiveStatus = (msg: IRtcStreamMessage) => {
        if (msg.payload.boolean !== undefined) {
            this.value = msg.payload.boolean;
        } else if (msg.payload.bitset !== undefined) {
            for (const bit of msg.payload.bitset.bits) {
                if (bit.key === this.props.statusLabel) {
                    this.value = bit.value || false;
                }
            }
        }
    };
}
